import { fetchApi } from "@/lib/api/index.ts";
import { useToast } from "@/components/ui/use-toast";
import {
  weekInZone,
  type ScheduleDay,
  type TimeOffEntry,
  type TimeOffType,
} from "@/lib/schedule/schedule-display";
import { getDeviceTimezone } from "@/lib/utils/timezone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useRef } from "react";
import { z } from "zod";

const scheduleDaySchema = z.object({
  day: z.string(),
  isOnline: z.boolean(),
  startTime: z.string(),
  endTime: z.string(),
});

const timeOffSchema = z
  .object({
    id: z.string(),
    type: z.enum(["pto", "emergency", "other"]),
    note: z.string().nullable(),
    starts_at: z.string(),
    ends_at: z.string(),
    ended_early_at: z.string().nullable(),
    pod_timezone: z.string(),
  })
  .transform(
    (entry): TimeOffEntry => ({
      id: entry.id,
      type: entry.type,
      note: entry.note,
      startsAt: entry.starts_at,
      endsAt: entry.ends_at,
      endedEarlyAt: entry.ended_early_at,
      podTimezone: entry.pod_timezone,
    }),
  );

const mentorScheduleSchema = z
  .object({
    billable_groups: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        timezone: z.string(),
        default_hours: z.array(scheduleDaySchema),
      }),
    ),
    schedule: z.array(scheduleDaySchema),
    timezone: z.string(),
    time_off: z.object({
      current: z.array(timeOffSchema),
      upcoming: z.array(timeOffSchema),
      past: z.array(timeOffSchema),
    }),
  })
  .transform((data) => ({
    billableGroups: data.billable_groups.map((group) => ({
      id: group.id,
      name: group.name,
      timezone: group.timezone,
      hours: group.default_hours,
    })),
    hours: data.schedule,
    localTimezone: data.timezone,
    timeOff: data.time_off,
  }));

export type MentorScheduleGroup = z.infer<
  typeof mentorScheduleSchema
>["billableGroups"][number];

interface TimeOffPayload {
  type: TimeOffType;
  note: string | null;
  startsAt: string;
  endsAt: string;
  timezone: string;
}

const scheduleKey = (userId?: string) => ["mentor-schedule", userId ?? "self"];

const scheduleUrl = (userId?: string) =>
  userId ? `/v2/users/${userId}/schedule` : "/v2/users/schedule";

const readMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return "Could not save that change";
};

export function useMentorSchedule(userId?: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: scheduleKey(userId),
    queryFn: async () => {
      const response = await fetchApi<unknown>({
        url: scheduleUrl(userId),
        method: "GET",
      });
      return mentorScheduleSchema.parse(response.data);
    },
  });
}

function useScheduleClient(userId?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: scheduleKey(userId) });
    if (userId) {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["current-user"] });
  };

  const fail = (error: unknown) => {
    toast({
      title: "Error",
      description: readMessage(error),
      variant: "destructive",
    });
  };

  return { toast, invalidate, fail };
}

export function useSaveSchedule(userId?: string) {
  const { toast, invalidate, fail } = useScheduleClient(userId);
  return useMutation({
    mutationFn: async (body: { schedule: ScheduleDay[]; timezone: string }) => {
      await fetchApi({
        url: scheduleUrl(userId),
        method: "PATCH",
        data: {
          schedule: body.schedule,
          timezone: body.timezone,
        },
      });
    },
    onSuccess: () => {
      toast({ title: "Schedule updated and saved." });
      invalidate();
    },
    onError: fail,
  });
}

// Moves the mentor's own week to the device timezone, keeping each shift at the same
// instants. A week that would put two shifts on one local day stays where it is.
export function useScheduleTimezoneSync(enabled: boolean) {
  const { data: schedule } = useMentorSchedule(undefined, enabled);
  const { invalidate } = useScheduleClient();
  const attemptedZone = useRef<string | null>(null);
  const { mutate } = useMutation({
    mutationFn: async (body: { schedule: ScheduleDay[]; timezone: string }) => {
      await fetchApi({ url: scheduleUrl(), method: "PATCH", data: body });
    },
    onSuccess: invalidate,
  });

  useEffect(() => {
    if (!enabled || !schedule) return;
    const deviceZone = getDeviceTimezone();
    if (
      schedule.localTimezone === deviceZone ||
      attemptedZone.current === deviceZone
    ) {
      return;
    }
    attemptedZone.current = deviceZone;
    const week = weekInZone(schedule.hours, schedule.localTimezone, deviceZone);
    if ("clash" in week) return;
    mutate({ schedule: week.hours, timezone: week.timezone });
  }, [enabled, schedule, mutate]);
}

export function useCreateTimeOff(userId?: string) {
  const { invalidate, fail } = useScheduleClient(userId);
  return useMutation({
    mutationFn: async (body: TimeOffPayload) => {
      await fetchApi({
        url: `${scheduleUrl(userId)}/time-off`,
        method: "POST",
        data: {
          type: body.type,
          note: body.note,
          starts_at: body.startsAt,
          ends_at: body.endsAt,
          timezone: body.timezone,
        },
      });
    },
    onSuccess: invalidate,
    onError: fail,
  });
}

export function useUpdateTimeOff(userId?: string) {
  const { invalidate, fail } = useScheduleClient(userId);
  return useMutation({
    mutationFn: async (body: TimeOffPayload & { id: string }) => {
      await fetchApi({
        url: `${scheduleUrl(userId)}/time-off/${body.id}`,
        method: "PATCH",
        data: {
          type: body.type,
          note: body.note,
          starts_at: body.startsAt,
          ends_at: body.endsAt,
          timezone: body.timezone,
        },
      });
    },
    onSuccess: invalidate,
    onError: fail,
  });
}

export function useDeleteTimeOff(userId?: string) {
  const { invalidate, fail } = useScheduleClient(userId);
  return useMutation({
    mutationFn: async (id: string) => {
      await fetchApi({
        url: `${scheduleUrl(userId)}/time-off/${id}`,
        method: "DELETE",
      });
    },
    onSuccess: invalidate,
    onError: fail,
  });
}

export function useCancelTimeOff(userId?: string) {
  const { invalidate, fail } = useScheduleClient(userId);
  return useMutation({
    mutationFn: async (id: string) => {
      await fetchApi({
        url: `${scheduleUrl(userId)}/time-off/${id}/cancel`,
        method: "POST",
      });
    },
    onSuccess: invalidate,
    onError: fail,
  });
}
